import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ChevronRight from "@ucs/Icons/ChevronRight";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { useState } from "react";
import { css } from "styled-components";

const enum fields {
    categoryP1 = "categoryP1",
    categoryP2 = "categoryP2",
    categoryP3 = "categoryP3",
    categoryP4 = "categoryP4",
    categoryP5 = "categoryP5",
    linkP1 = "linkP1",
    linkP2 = "linkP2",
    linkP3 = "linkP3",
    linkP4 = "linkP4",
    linkP5 = "linkP5",
    linkTitleP1 = "linkTitleP1",
    linkTitleP2 = "linkTitleP2",
    linkTitleP3 = "linkTitleP3",
    linkTitleP4 = "linkTitleP4",
    linkTitleP5 = "linkTitleP5",
    openInNewWindow1 = "openInNewWindow1",
    openInNewWindow2 = "openInNewWindow2",
    openInNewWindow3 = "openInNewWindow3",
    openInNewWindow4 = "openInNewWindow4",
    openInNewWindow5 = "openInNewWindow5",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.categoryP1]: string;
        [fields.categoryP2]: string;
        [fields.categoryP3]: string;
        [fields.categoryP4]: string;
        [fields.categoryP5]: string;
        [fields.linkP1]: LinkFieldValue;
        [fields.linkP2]: LinkFieldValue;
        [fields.linkP3]: LinkFieldValue;
        [fields.linkP4]: LinkFieldValue;
        [fields.linkP5]: LinkFieldValue;
        [fields.linkTitleP1]: string;
        [fields.linkTitleP2]: string;
        [fields.linkTitleP3]: string;
        [fields.linkTitleP4]: string;
        [fields.linkTitleP5]: string;
        [fields.openInNewWindow1]: string;
        [fields.openInNewWindow2]: string;
        [fields.openInNewWindow3]: string;
        [fields.openInNewWindow4]: string;
        [fields.openInNewWindow5]: string;
    };
    extendedStyles?: ProductListingStyles;
}

export interface ProductListingStyles {
    wrapper?: InjectableCss;
    row?: InjectableCss;
    columns?: InjectableCss;
    leftColumn?: InjectableCss;
    rightColumn?: InjectableCss;
    category?: TypographyProps;
    link?: LinkProps;
}

export const applicationsStyles: ProductListingStyles = {
    wrapper: {
        css: css`
            width: 100%;
            & > :first-child {
                border-top: 2px solid ${UCSColors.gray07};
            }
        `,
    },
    row: {
        css: css`
            width: 100%;
            padding: 16px 0;
            display: none;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            border-bottom: 2px solid ${UCSColors.gray07};
            color: ${UCSColors.gray03};
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        display: flex;
                        padding: 16px 10px;
                    `,
                    css`
                        display: flex;
                        padding: 16px 10px;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: flex;
                    `,
                ]);
            }}
        `,
    },
    columns: {
        css: css`
            width: 100%;
            display: flex;
            align-items: flex-start;
            color: ${UCSColors.gray03};
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: flex;
                    `,
                    css`
                        display: none;
                    `,
                ]);
            }}
            & > div {
                border-top: 2px solid ${UCSColors.gray07};
            }
        `,
    },
    leftColumn: {
        css: css`
            width: fit-content;
            display: flex;
            flex-direction: column;
            & > * {
                display: flex;
                align-items: center;
                height: 60px;
                padding-right: 50px;
                border-bottom: 2px solid ${UCSColors.gray07};
            }
        `,
    },
    rightColumn: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            & > * {
                display: flex;
                align-items: center;
                height: 60px;
                border-bottom: 2px solid ${UCSColors.gray07} !important;
            }
        `,
    },
    category: {
        variant: "p",
        size: 13,
        weight: 800,
        transform: "uppercase",
        css: css`
            display: flex;
            line-height: 16px;
            letter-spacing: 0.1em;
            margin: 0;
            width: 100%;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 12px;
                    `,
                    css`
                        font-size: 12px;
                    `,
                ]);
            }}
        `,
    },
    link: {
        icon: {
            iconProps: { color: "primary", size: 16, src: ChevronRight },
        },
        typographyProps: {
            size: 18,
            css: css`
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            font-size: 17px;
                        `,
                        css`
                            font-size: 17px;
                        `,
                    ]);
                }}
            `,
        },
    },
};

type Props = OwnProps;

const ProductListing: React.FC<Props> = ({ fields, extendedStyles }) => {
    const {
        categoryP1,
        categoryP2,
        categoryP3,
        categoryP4,
        categoryP5,
        linkTitleP1,
        linkTitleP2,
        linkTitleP3,
        linkTitleP4,
        linkTitleP5,
    } = fields;
    const linkP1 = useGetLink(fields.linkP1);
    const linkP2 = useGetLink(fields.linkP2);
    const linkP3 = useGetLink(fields.linkP3);
    const linkP4 = useGetLink(fields.linkP4);
    const linkP5 = useGetLink(fields.linkP5);

    const [styles] = useState(() => mergeToNew(applicationsStyles, extendedStyles));

    return (
        <StyledWrapper {...styles.wrapper}>
            <ProductRow category={categoryP1} url={linkP1.url || ""} label={linkTitleP1} styles={styles} />
            <ProductRow category={categoryP2} url={linkP2.url || ""} label={linkTitleP2} styles={styles} />
            <ProductRow category={categoryP3} url={linkP3.url || ""} label={linkTitleP3} styles={styles} />
            <ProductRow category={categoryP4} url={linkP4.url || ""} label={linkTitleP4} styles={styles} />
            <ProductRow category={categoryP5} url={linkP5.url || ""} label={linkTitleP5} styles={styles} />

            <StyledWrapper {...styles.columns}>
                <StyledWrapper {...styles.leftColumn}>
                    {categoryP1 && <Typography {...styles.category}>{categoryP1}</Typography>}
                    {categoryP2 && <Typography {...styles.category}>{categoryP2}</Typography>}
                    {categoryP3 && <Typography {...styles.category}>{categoryP3}</Typography>}
                    {categoryP4 && <Typography {...styles.category}>{categoryP4}</Typography>}
                    {categoryP5 && <Typography {...styles.category}>{categoryP5}</Typography>}
                </StyledWrapper>
                <StyledWrapper {...styles.rightColumn}>
                    {categoryP1 && (
                        <Link {...styles.link} href={linkP1.url || ""} target={fields.openInNewWindow1 ? "_blank" : ""}>
                            {linkTitleP1}
                        </Link>
                    )}
                    {categoryP2 && (
                        <Link {...styles.link} href={linkP2.url || ""} target={fields.openInNewWindow2 ? "_blank" : ""}>
                            {linkTitleP2}
                        </Link>
                    )}
                    {categoryP3 && (
                        <Link {...styles.link} href={linkP3.url || ""} target={fields.openInNewWindow3 ? "_blank" : ""}>
                            {linkTitleP3}
                        </Link>
                    )}
                    {categoryP4 && (
                        <Link {...styles.link} href={linkP4.url || ""} target={fields.openInNewWindow4 ? "_blank" : ""}>
                            {linkTitleP4}
                        </Link>
                    )}
                    {categoryP5 && (
                        <Link {...styles.link} href={linkP5.url || ""} target={fields.openInNewWindow5 ? "_blank" : ""}>
                            {linkTitleP5}
                        </Link>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

interface ProductRowProps {
    category: string;
    url: string;
    label: string;
    styles: ProductListingStyles;
}

const ProductRow: React.FC<ProductRowProps> = ({ category, url, label, styles }) => {
    if (!category && !url && !label) {
        return null;
    }

    return (
        <StyledWrapper {...styles.row}>
            <Typography {...styles.category}>{category}</Typography>
            <Link {...styles.link} href={url}>
                {label}
            </Link>
        </StyledWrapper>
    );
};

const tab1 = { displayName: "P1", sortOrder: 0 };
const tab2 = { displayName: "P2", sortOrder: 1 };
const tab3 = { displayName: "P3", sortOrder: 2 };
const tab4 = { displayName: "P4", sortOrder: 3 };
const tab5 = { displayName: "P5", sortOrder: 4 };

const widgetModule: WidgetModule = {
    component: ProductListing,
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.categoryP1,
                displayName: "Category",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tab1,
            },
            {
                name: fields.linkTitleP1,
                displayName: "Link Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tab1,
            },
            {
                name: fields.linkP1,
                displayName: "Product Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                tab: tab1,
            },
            {
                name: fields.openInNewWindow1,
                displayName: "Open In New Window",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Translatable",
                tab: tab1,
            },
            {
                name: fields.categoryP2,
                displayName: "Category",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tab2,
            },
            {
                name: fields.linkTitleP2,
                displayName: "Link Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tab2,
            },
            {
                name: fields.linkP2,
                displayName: "Product Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                tab: tab2,
            },
            {
                name: fields.openInNewWindow2,
                displayName: "Open In New Window",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Translatable",
                tab: tab2,
            },
            {
                name: fields.categoryP3,
                displayName: "Category",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tab3,
            },
            {
                name: fields.linkTitleP3,
                displayName: "Link Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tab3,
            },
            {
                name: fields.linkP3,
                displayName: "Product Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                tab: tab3,
            },
            {
                name: fields.openInNewWindow3,
                displayName: "Open In New Window",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Translatable",
                tab: tab3,
            },
            {
                name: fields.categoryP4,
                displayName: "Category",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tab4,
            },
            {
                name: fields.linkTitleP4,
                displayName: "Link Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tab4,
            },
            {
                name: fields.linkP4,
                displayName: "Product Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                tab: tab4,
            },
            {
                name: fields.openInNewWindow4,
                displayName: "Open In New Window",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Translatable",
                tab: tab4,
            },
            {
                name: fields.categoryP5,
                displayName: "Category",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tab5,
            },
            {
                name: fields.linkTitleP5,
                displayName: "Link Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tab5,
            },
            {
                name: fields.linkP5,
                displayName: "Product Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                tab: tab5,
            },
            {
                name: fields.openInNewWindow5,
                displayName: "Open In New Window",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Translatable",
                tab: tab5,
            },
        ],
    },
};

export default widgetModule;
